import React, { useState, useCallback } from 'react';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TableChartIcon from '@mui/icons-material/TableChart';
import DescriptionIcon from '@mui/icons-material/Description';
// ExcelJS is loaded lazily (dynamic import) inside the Excel export handler so it
// is not pulled into the main bundle. Replaces the unmaintained/vulnerable `xlsx`.

/**
 * ExportButton — Enterprise Design System
 *
 * A dropdown button providing CSV and Excel export options.
 * Exports the currently provided rows (already filtered/sorted by the parent).
 * Enforces a maximum row limit (default 10,000) and shows error toast on failure.
 *
 * Props:
 *  - rows: array of data objects to export
 *  - columns: array of { field, headerName } defining which fields to export and their headers
 *  - filename: base filename for the exported file (default 'export')
 *  - maxRows: maximum number of rows allowed for export (default 10000)
 */
function ExportButton({ rows = [], columns = [], filename = 'export', maxRows = 10000 }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [error, setError] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCloseError = () => {
    setError(null);
  };

  /**
   * Build a 2D array from rows using column definitions.
   * First row is headers, subsequent rows are data values.
   */
  const buildExportData = useCallback(() => {
    const headers = columns.map((col) => col.headerName || col.field);
    const data = rows.map((row) =>
      columns.map((col) => {
        const value = row[col.field];
        return value !== null && value !== undefined ? value : '';
      })
    );
    return [headers, ...data];
  }, [rows, columns]);

  /**
   * Trigger a browser download for the given blob.
   */
  const triggerDownload = (blob, downloadFilename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = downloadFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /**
   * Export as CSV — generates comma-separated file and triggers download.
   */
  const handleExportCSV = () => {
    handleClose();

    if (rows.length > maxRows) {
      setError(`Export limited to ${maxRows.toLocaleString()} rows. Current data has ${rows.length.toLocaleString()} rows.`);
      return;
    }

    try {
      const exportData = buildExportData();
      const csvContent = exportData
        .map((row) =>
          row
            .map((cell) => {
              const str = String(cell);
              // Escape cells containing commas, quotes, or newlines
              if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                return `"${str.replace(/"/g, '""')}"`;
              }
              return str;
            })
            .join(',')
        )
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      triggerDownload(blob, `${filename}.csv`);
    } catch (err) {
      setError('Failed to export CSV. Please try again.');
    }
  };

  /**
   * Export as Excel — uses xlsx library to generate .xlsx and triggers download.
   */
  const handleExportExcel = async () => {
    handleClose();

    if (rows.length > maxRows) {
      setError(`Export limited to ${maxRows.toLocaleString()} rows. Current data has ${rows.length.toLocaleString()} rows.`);
      return;
    }

    try {
      // exportData is an array-of-arrays: [ [header...], [row...], ... ]
      const exportData = buildExportData();
      const { default: ExcelJS } = await import('exceljs');
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Data');
      exportData.forEach((row) => worksheet.addRow(row));
      const excelBuffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      triggerDownload(blob, `${filename}.xlsx`);
    } catch (err) {
      setError('Failed to export Excel file. Please try again.');
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<FileDownloadIcon />}
        onClick={handleClick}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        aria-label="Export data"
        sx={{ textTransform: 'none', fontSize: '0.8125rem' }}
      >
        Export
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleExportCSV}>
          <ListItemIcon>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Export as CSV</Typography>
        </MenuItem>
        <MenuItem onClick={handleExportExcel}>
          <ListItemIcon>
            <TableChartIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Export as Excel</Typography>
        </MenuItem>
      </Menu>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" variant="filled" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ExportButton;
